import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, CreditCard, Building } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  originalAmount: number;
  currency: string;
  showDetailed?: boolean;
  feeResponsibility?: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage?: number | null;
  gatewayFeePercentage?: number | null;
}

export default function TransactionBreakdown({ 
  originalAmount, 
  currency, 
  showDetailed = false,
  feeResponsibility = 'GUEST',
  platformFeePercentage,
  gatewayFeePercentage
}: Props) {
  const [platformFeeRate, setPlatformFeeRate] = useState(0);
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (platformFeePercentage !== undefined && platformFeePercentage !== null && 
        gatewayFeePercentage !== undefined && gatewayFeePercentage !== null) {
      console.log('TransactionBreakdown - Using provided percentages:', {
        platformFeePercentage,
        gatewayFeePercentage,
        originalAmount,
        feeResponsibility,
        isAlreadyDecimal: platformFeePercentage < 1 && gatewayFeePercentage < 1
      });
      const platformDecimal = platformFeePercentage < 1 ? platformFeePercentage : platformFeePercentage / 100;
      const gatewayDecimal = gatewayFeePercentage < 1 ? gatewayFeePercentage : gatewayFeePercentage / 100;
      
      console.log('TransactionBreakdown - Converted to decimal rates:', {
        platformDecimal,
        gatewayDecimal
      });
      
      setPlatformFeeRate(platformDecimal);
      setGatewayFeeRate(gatewayDecimal);
      setLoading(false);
    } else {
      console.log('TransactionBreakdown - Loading from database, provided values:', {
        platformFeePercentage,
        gatewayFeePercentage
      });
      loadFeeRates();
    }
  }, [platformFeePercentage, gatewayFeePercentage]);
  
  const loadFeeRates = async () => {
    try {
      setLoading(true);
      
      // Get platform fee rate
      const { data: platformFeeData, error: platformFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .single();
        
      if (!platformFeeError && platformFeeData) {
        console.log('TransactionBreakdown - Platform fee from DB:', platformFeeData.value);
        setPlatformFeeRate(parseFloat(platformFeeData.value) / 100);
      }
      
      // Get gateway fee rate
      const { data: gatewayFeeData, error: gatewayFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .single();
        
      if (!gatewayFeeError && gatewayFeeData) {
        console.log('TransactionBreakdown - Gateway fee from DB:', gatewayFeeData.value);
        setGatewayFeeRate(parseFloat(gatewayFeeData.value) / 100);
      }
    } catch (error) {
      console.error('Error loading fee rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const platformFee = originalAmount * platformFeeRate;
  const gatewayFee = originalAmount * gatewayFeeRate;
  const totalFees = platformFee + gatewayFee;
  
  console.log('TransactionBreakdown - Fee calculations:', {
    originalAmount,
    platformFeeRate,
    gatewayFeeRate,
    platformFee,
    gatewayFee,
    totalFees,
    feeResponsibility
  });
  
  let guestPays = originalAmount;
  let coupleReceives = originalAmount;
  let displayedFeePercentage = 0;
  
  switch (feeResponsibility) {
    case 'GUEST':
      guestPays = originalAmount + totalFees;
      coupleReceives = originalAmount;
      displayedFeePercentage = (platformFeeRate + gatewayFeeRate) * 100;
      break;
    case 'COUPLE':
      guestPays = originalAmount;
      coupleReceives = originalAmount - totalFees;
      displayedFeePercentage = 0; // Don't show fees to guests
      break;
    case 'SPLIT':
      guestPays = originalAmount + (totalFees / 2);
      coupleReceives = originalAmount - (totalFees / 2);
      displayedFeePercentage = (platformFeeRate + gatewayFeeRate) * 50; // Show half the fee percentage
      break;
  }

  const LKR_TO_USD_RATE = 320;

  const formatAmount = (amount: number) => {
    return `${currency} ${amount.toFixed(2)}`;
  };

  const formatWithUSD = (amount: number) => {
    if (currency !== 'LKR') return formatAmount(amount);
    const usd = (amount / LKR_TO_USD_RATE).toFixed(2);
    return `LKR ${amount.toFixed(2)} (≈ USD ${usd})`;
  };

  if (loading) {
    return (
      <div className="bg-gray-900/50 p-4 rounded-lg">
        <div className="flex items-center justify-center space-x-2 text-gray-400">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          <span>Loading payment information...</span>
        </div>
      </div>
    );
  }

  // For Couple Absorbs Fee option, don't show any fee information
  if (feeResponsibility === 'COUPLE' && !showDetailed) {
    return (
      <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 text-white mb-2">
          <Calculator className="w-4 h-4" />
          <span className="font-medium">Payment Summary</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-300">Gift Amount:</span>
            <span className="text-white font-medium">{formatAmount(originalAmount)}</span>
          </div>
          <div className="border-t border-gray-700 pt-2 flex justify-between">
            <span className="text-white font-medium">Total:</span>
            <span className="text-white font-bold">{formatWithUSD(guestPays)}</span>
          </div>
          {currency === 'LKR' && (
            <p className="text-xs text-gray-400 pt-1">Payment is processed in USD via PayPal at a fixed rate of 1 USD = 320 LKR.</p>
          )}
        </div>
      </div>
    );
  }

  // Simple version for gift claim mode (Guest Pays or Split)
  if (!showDetailed) {
    return (
      <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 text-white mb-2">
          <Calculator className="w-4 h-4" />
          <span className="font-medium">Payment Summary</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-300">Gift Amount:</span>
            <span className="text-white font-medium">{formatAmount(originalAmount)}</span>
          </div>
          {displayedFeePercentage > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-300">
                {feeResponsibility === 'SPLIT' ? 'Your Share of Service Fee' : 'Service Fee'} ({displayedFeePercentage.toFixed(1)}%):
              </span>
              <span className="text-white font-medium">
                {feeResponsibility === 'SPLIT'
                  ? formatAmount(totalFees / 2)
                  : formatAmount(totalFees)
                }
              </span>
            </div>
          )}
          <div className="border-t border-gray-700 pt-2 flex justify-between">
            <span className="text-white font-medium">Total:</span>
            <span className="text-white font-bold">{formatWithUSD(guestPays)}</span>
          </div>
          {currency === 'LKR' && (
            <p className="text-xs text-gray-400 pt-1">Payment is processed in USD via PayPal at a fixed rate of 1 USD = 320 LKR.</p>
          )}
        </div>
      </div>
    );
  }

  // Detailed version for admin/settings
  return (
    <div className="bg-gray-900/50 p-6 rounded-lg space-y-4">
      <div className="flex items-center space-x-2 text-white mb-4">
        <Calculator className="w-5 h-5" />
        <span className="font-semibold">Payment Breakdown</span>
      </div>
      
      <div className="space-y-4">
        <div className="bg-gray-800/50 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-blue-400 mb-3">
            <DollarSign className="w-4 h-4" />
            <span className="font-medium">Gift Amount</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {formatAmount(originalAmount)}
          </div>
        </div>

        <div className="bg-gray-800/50 p-4 rounded-lg space-y-3">
          <div className="flex items-center space-x-2 text-orange-400 mb-3">
            <span className="font-medium">Service Fees</span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Platform fee ({(platformFeeRate * 100).toFixed(1)}%):</span>
              <span>{formatAmount(platformFee)}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Gateway fee ({(gatewayFeeRate * 100).toFixed(1)}%):</span>
              <span>{formatAmount(gatewayFee)}</span>
            </div>
            <div className="border-t border-gray-600 pt-2 flex justify-between font-medium text-orange-400">
              <span>Total fees:</span>
              <span>{formatAmount(totalFees)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-green-400 mb-3">
            <span className="font-medium">Fee Responsibility</span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Guest pays:</span>
              <span>{formatAmount(guestPays)}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Couple receives:</span>
              <span>{formatAmount(coupleReceives)}</span>
            </div>
            <div className="border-t border-gray-600 pt-2 flex justify-between font-medium text-green-400">
              <span>Fee responsibility:</span>
              <span>
                {feeResponsibility === 'GUEST' ? 'Guest Pays' : 
                 feeResponsibility === 'COUPLE' ? 'Couple Absorbs' : 
                 'Split 50/50'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}